﻿import * as signalR from '@microsoft/signalr';
import config from '../config';
import { IConnectionRequest } from '../interfaces/IChatUser';

type ChatHubEvents = {
    ReceiveMessage: (userId: string, message: string, conversationId: string | null, messageId: string | null, createdAtIso?: string) => void;
    NewConnectionRequest: (request: IConnectionRequest) => void;
    UserOnline: (userId: string) => void;
    UserOffline: (userId: string) => void;
    UserTyping: (userId: string, conversationId?: string | null) => void;
    UserStoppedTyping: (userId: string, conversationId?: string | null) => void;
    MessagesRead: (conversationId: string, readerUserId: string, lastReadMessageId?: string) => void;
    MessageReactionChanged: (payload: { messageId: string; userId: string; action: string; reactionType: string; counts: Record<string, number> }) => void;
    ConnectionAccepted: (userId: string) => void; // other user id
    ConnectionRejected: (userId: string) => void; // other user id
    ConnectionCanceled: (userId: string) => void; // other user id (outgoing canceled)
    ConnectionRemoved: (userId: string) => void; // accepted connection removed
};

class ChatHubService {
private connection: signalR.HubConnection;
private starting = false;
private isStopped = false;
private restartTimer: ReturnType<typeof setTimeout> | null = null;

constructor(authToken: string) {
    this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${config.apiServerUrl}/chatHub`, {
            accessTokenFactory: () => authToken,
            transport: signalR.HttpTransportType.WebSockets
        })
        // Retry indefinitely with a capped, jittered backoff. A fixed short
        // list gives up after a few attempts and leaves the client silently
        // disconnected: REST still works, but no SignalR pushes (messages,
        // typing, read receipts) arrive until the page is reloaded.
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
                const schedule = [0, 2000, 5000, 10000];
                const base = retryContext.previousRetryCount < schedule.length
                    ? schedule[retryContext.previousRetryCount]
                    : 15000;
                // Jitter avoids a thundering herd when the server restarts.
                return base + Math.floor(Math.random() * 3000);
            },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

    // Detect half-open connections faster than the 30s default so automatic
    // reconnect can kick in instead of believing a dead socket is still alive.
    this.connection.serverTimeoutInMilliseconds = 30000;
    this.connection.keepAliveIntervalInMilliseconds = 10000;

    this.connection.onreconnecting(() => console.log('ChatHub: Reconnecting...'));
    this.connection.onreconnected(() => console.log('ChatHub: Reconnected'));
    this.connection.onclose(() => {
        console.log('ChatHub: Connection closed');
        // onclose fires when automatic reconnection has been exhausted or an
        // unrecoverable error occurred. Unless we stopped intentionally, try to
        // re-establish so the chat keeps receiving live events.
        if (!this.isStopped) {
            this.scheduleRestart();
        }
    });
}

private scheduleRestart(): void {
    if (this.restartTimer) {
        return;
    }
    this.restartTimer = setTimeout(() => {
        this.restartTimer = null;
        if (this.isStopped) {
            return;
        }
        this.start().catch(() => {
            if (!this.isStopped) {
                this.scheduleRestart();
            }
        });
    }, 5000);
}

async start(): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Connected ||
        this.connection.state === signalR.HubConnectionState.Connecting ||
        this.starting) {
        return; // Already starting or connected
    }

    this.starting = true;
    this.isStopped = false;
    try {
        console.log('Starting SignalR connection...');
        await this.connection.start();
        console.log('Connected to ChatHub');
    } catch (err) {
        console.error('SignalR start error:', err);
        // Keep trying until we either connect or are intentionally stopped.
        if (!this.isStopped) {
            this.scheduleRestart();
        }
    } finally {
        this.starting = false;
    }
}

async stop(): Promise<void> {
    this.isStopped = true;

    // Cancel any pending restart attempts
    if (this.restartTimer) {
        clearTimeout(this.restartTimer);
        this.restartTimer = null;
    }

    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
        await this.connection.stop();
        console.log('Disconnected from ChatHub');
    }
}

    /**
     * Register an additive listener. Returns an unsubscribe function that removes
     * ONLY this handler, so multiple consumers can subscribe to the same event
     * without tearing each other's handlers down.
     */
    on<K extends keyof ChatHubEvents>(event: K, handler: ChatHubEvents[K]): () => void {
        this.connection.on(event, handler);
        return () => this.connection.off(event, handler);
    }

    off<K extends keyof ChatHubEvents>(event: K, handler?: ChatHubEvents[K]): void {
        if (handler) {
            this.connection.off(event, handler);
        } else {
            this.connection.off(event);
        }
    }

    async sendTyping(toUserId: string): Promise<void> {
        console.log('🔥 Frontend sendTyping called with toUserId:', toUserId);
        await this.connection.invoke('Typing', toUserId);
        console.log('✅ Frontend sendTyping invoke completed');
    }

    async sendStopTyping(toUserId: string): Promise<void> {
        await this.connection.invoke('StopTyping', toUserId);
    }

    /**
     * Join a conversation group to receive real-time updates for that conversation.
     * Call this when opening a chat window.
     */
    async joinConversationGroup(conversationId: string): Promise<void> {
        await this.connection.invoke('JoinConversationGroup', conversationId);
        console.log(`✅ Joined conversation group: ${conversationId}`);
    }

    /**
     * Leave a conversation group when closing a chat window.
     */
    async leaveConversationGroup(conversationId: string): Promise<void> {
        try {
            await this.connection.invoke('LeaveConversationGroup', conversationId);
            console.log(`👋 Left conversation group: ${conversationId}`);
        } catch (error) {
            console.error(`Failed to leave conversation group ${conversationId}:`, error);
            // Don't throw - leaving is best-effort
        }
    }

    // Optional presence snapshot helper. Server must implement 'GetOnlineUsers' returning string[]
    async getOnlineUsers(): Promise<string[]> {
        try {
            const users = await this.connection.invoke<string[]>('GetOnlineUsers');
            return Array.isArray(users) ? users : [];
        } catch (e) {
            // If server doesn't support it, return empty list gracefully
            return [];
        }
    }

    // Debug method to get connection info
    async getConnectionDebugInfo(): Promise<any> {
        try {
            const info = await this.connection.invoke('GetConnectionDebugInfo');
            console.log('🔍 Connection Debug Info:', info);
            return info;
        } catch (e) {
            console.error('Failed to get connection debug info:', e);
            return null;
        }
    }

    getConnection(): signalR.HubConnection {
        return this.connection;
    }
}

export const createChatHubService = (authToken: string) => new ChatHubService(authToken);